import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  ControlledDocument,
  DocumentAcknowledgmentsResponse,
  DocumentRevision,
  PaginatedResponse,
} from '@/lib/types';

const DOCS_API = '/api/v1/compliance/documents';

/**
 * A document's related clauses feed the Comply clause pages ("Needs document"
 * hints, linked records, section rows, the landing score), so those views
 * must refetch after a document is created or edited.
 */
function invalidateClauseViews(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: queryKeys.clauseStatus.all });
  qc.invalidateQueries({ queryKey: queryKeys.compliance.all });
}

export interface ControlledDocumentOwnerOption {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
}

// ─── Filter Types ───────────────────────────────────────────────────────────

export interface DocumentFilters {
  page?: number;
  page_size?: number;
  search?: string;
  category?: string;
  status?: string;
  overdue?: boolean;
  ordering?: string;
}

function cleanFilters<T extends object>(filters: T): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );
}

// ─── List / Detail ──────────────────────────────────────────────────────────

export function useControlledDocuments(filters: DocumentFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.controlledDocuments.list(clean),
    queryFn: async (): Promise<PaginatedResponse<ControlledDocument>> => {
      const response = await apiClient.get<PaginatedResponse<ControlledDocument>>(
        `${DOCS_API}/`,
        { params: clean },
      );
      return response.data;
    },
  });
}

export function useControlledDocument(id: number) {
  return useQuery({
    queryKey: queryKeys.controlledDocuments.detail(id),
    queryFn: async (): Promise<ControlledDocument> => {
      const response = await apiClient.get<ControlledDocument>(`${DOCS_API}/${id}/`);
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useControlledDocumentOwnerOptions() {
  return useQuery({
    queryKey: queryKeys.controlledDocuments.ownerOptions(),
    queryFn: async (): Promise<ControlledDocumentOwnerOption[]> => {
      const response = await apiClient.get<ControlledDocumentOwnerOption[]>(
        `${DOCS_API}/owner-options/`,
      );
      return response.data;
    },
  });
}

// ─── Create ─────────────────────────────────────────────────────────────────

export function useCreateControlledDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (formData: FormData): Promise<ControlledDocument> => {
      const response = await apiClient.post<ControlledDocument>(`${DOCS_API}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.controlledDocuments.all });
      invalidateClauseViews(qc);
    },
  });
}

export function useCreateAndApproveControlledDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (formData: FormData): Promise<ControlledDocument> => {
      const response = await apiClient.post<ControlledDocument>(
        `${DOCS_API}/create-and-approve/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.controlledDocuments.all });
      invalidateClauseViews(qc);
    },
  });
}

// ─── Update ─────────────────────────────────────────────────────────────────

export function useUpdateControlledDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, formData }: { id: number; formData: FormData }): Promise<ControlledDocument> => {
      const response = await apiClient.patch<ControlledDocument>(`${DOCS_API}/${id}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.controlledDocuments.all });
      qc.invalidateQueries({ queryKey: queryKeys.controlledDocuments.detail(variables.id) });
      invalidateClauseViews(qc);
    },
  });
}

// ─── Delete ─────────────────────────────────────────────────────────────────

export function useDeleteControlledDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${DOCS_API}/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.controlledDocuments.all });
      invalidateClauseViews(qc);
    },
  });
}

// ─── Workflow Actions ───────────────────────────────────────────────────────

export function useUploadRevision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, formData }: { id: number; formData: FormData }): Promise<DocumentRevision> => {
      const response = await apiClient.post<DocumentRevision>(
        `${DOCS_API}/${id}/upload-revision/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.controlledDocuments.all });
      qc.invalidateQueries({ queryKey: queryKeys.controlledDocuments.detail(variables.id) });
    },
  });
}

export function useSubmitForReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<ControlledDocument> => {
      const response = await apiClient.post<ControlledDocument>(
        `${DOCS_API}/${id}/submit-for-review/`,
      );
      return response.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.controlledDocuments.all });
      qc.invalidateQueries({ queryKey: queryKeys.controlledDocuments.detail(id) });
    },
  });
}

export function useApproveDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<ControlledDocument> => {
      const response = await apiClient.post<ControlledDocument>(
        `${DOCS_API}/${id}/approve/`,
      );
      return response.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.controlledDocuments.all });
      qc.invalidateQueries({ queryKey: queryKeys.controlledDocuments.detail(id) });
    },
  });
}

export function useAcknowledgeDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<{ detail: string }> => {
      const response = await apiClient.post<{ detail: string }>(
        `${DOCS_API}/${id}/acknowledge/`,
      );
      return response.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.controlledDocuments.detail(id) });
      qc.invalidateQueries({ queryKey: queryKeys.controlledDocuments.acknowledgments(id) });
    },
  });
}

// ─── Acknowledgments ────────────────────────────────────────────────────────

export function useDocumentAcknowledgments(id: number) {
  return useQuery({
    queryKey: queryKeys.controlledDocuments.acknowledgments(id),
    queryFn: async (): Promise<DocumentAcknowledgmentsResponse> => {
      const response = await apiClient.get<DocumentAcknowledgmentsResponse>(
        `${DOCS_API}/${id}/acknowledgments/`,
      );
      return response.data;
    },
    enabled: id > 0,
  });
}
